import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { PolicyStackAttachment } from "types/generated";

import { ATTACH_POLICY_TO_ENTITY } from "./gql";

/**
 * Hook for attaching policies to Stack and Module entities.
 *
 * Note: For IntentProject entities, use `useAttachPolicyToIntentProject`
 * from `shared/IntentProject/useAttachPolicyToIntentProject` instead.
 * We may decide to merge these hooks in the future.
 */
const useAttachPolicy = (refetchQueries?: string[]) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [attachPolicy, { loading }] = useMutation<{
    policyAttach: PolicyStackAttachment;
  }>(ATTACH_POLICY_TO_ENTITY, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleAttachPolicy = useCallback(
    (policyId: string, entityId: string, successCallback?: () => void) => {
      attachPolicy({
        variables: {
          policyId,
          entityId,
        },
      })
        .then(({ data }) => {
          if (data?.policyAttach) {
            reportSuccess({
              message: "Policy was successfully attached",
            });
            successCallback?.();
          }
        })
        .catch(onError);
    },
    [attachPolicy, onError, reportSuccess]
  );

  return {
    attachPolicy: handleAttachPolicy,
    loading,
  };
};

export default useAttachPolicy;
